import { useContext, useEffect, useId, useRef, useState } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '@/components/SubmitButton';
import { OwnedEnvelopesProvider } from './OwnedEnvelopesProvider';
import { OwnedEnvelopesContext, useOwnedEnvelopes } from './useOwnedEnvelopes';

interface ReimbursementTargetPickerProps {
  /** The resend is in flight. */
  busy: boolean;
  /** Resends the confirm with the picked envelope. */
  onConfirm: (envelopeId: number) => void;
  /** Leaves the link as it was. */
  onCancel: () => void;
}

/**
 * A payback whose purchase has no envelope (`409 REIMBURSEMENT_TARGET_REQUIRED`, TMMAPI-0179) asks
 * which envelope the purchase came from, in place (TMMAPP-0140 item 3): the owner's envelopes, then
 * Confirm, which resends the confirm with that envelope. Review's quick confirm and the account
 * page's panel both use it. An account page provides the owned envelopes; elsewhere (the Planned
 * page) the picker brings its own provider, as the review drawer does.
 */
export function ReimbursementTargetPicker(props: ReimbursementTargetPickerProps) {
  const provided = useContext(OwnedEnvelopesContext) !== null;
  return provided ? (
    <TargetPicker {...props} />
  ) : (
    <OwnedEnvelopesProvider>
      <TargetPicker {...props} />
    </OwnedEnvelopesProvider>
  );
}

function TargetPicker({ busy, onConfirm, onCancel }: ReimbursementTargetPickerProps) {
  const { t } = useTranslation();
  const { envelopes } = useOwnedEnvelopes();
  const promptId = useId();
  const labelId = useId();
  const [envelopeId, setEnvelopeId] = useState<number | ''>('');
  const promptRef = useRef<HTMLParagraphElement>(null);

  // The picker replaced the action in place: focus starts on its question.
  useEffect(() => {
    promptRef.current?.focus();
  }, []);

  return (
    <Stack spacing={1} role="group" aria-labelledby={promptId} data-reimb-target="">
      <Typography id={promptId} ref={promptRef} tabIndex={-1} variant="body2">
        {t('reimb.targetPrompt')}
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel id={labelId}>{t('reimb.targetEnvelope')}</InputLabel>
        <Select
          labelId={labelId}
          label={t('reimb.targetEnvelope')}
          value={envelopeId === '' ? '' : String(envelopeId)}
          onChange={(event) => {
            const raw = event.target.value;
            setEnvelopeId(raw === '' ? '' : Number(raw));
          }}
        >
          {envelopes.map((envelope) => (
            <MenuItem key={envelope.id} value={String(envelope.id)}>
              {envelope.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <SubmitButton
          size="small"
          loading={busy}
          disabled={envelopeId === ''}
          onClick={() => {
            if (envelopeId !== '') onConfirm(envelopeId);
          }}
        >
          {t('reimb.confirm')}
        </SubmitButton>
        <Button size="small" disabled={busy} onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}
